import { UpdatePilotRotationSettingsRequest, tryUpdateAppSettingsV2 } from '@/actions/appSettings';
import { AppSettingsV2Updatable } from '@/actions/updatable';
import { integrationSelectOptions } from '@/helpers/integrationSelectHelpers';
import { RootState } from '@/reducers/reducers';
import {
    selectIntegrationsForCapability,
    selectIsPilotRotationSyncEnabled,
    selectPilotRotationSettings,
    selectReleasePilotRotationRepeatCount,
    selectShouldAssignCurrentOnCallToAllActiveReleases,
    selectShouldRotatePilotForEachReleaseFromSchedule,
} from '@/selectors/selectors';
import { AppComponentProps } from '@/typescriptHelpers';
import { globalMessage } from '@/utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Form from 'antd/lib/form';
import InputNumber from 'antd/lib/input-number';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { IntegrationCapability, PilotRotationSettings } from '../../types';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';

const rotatesPilotForEachReleaseOverrideMessage = (
    <>
        Your app has an existing internal configuration that overrides this setting. Each release is piloted by the next
        on-call in the external schedule, based on kickoff date, and hotfixes and rollbacks keep their previous release
        pilot.
        <br />
        <br />
        Contact us if you&apos;d like to remove this override.
    </>
);

interface PilotRotationSettingsModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
    workflowId: string;
}

interface PilotRotationSettingsFormValues extends PilotRotationSettings {
    keepPreviousReleasePilotForNonStandardReleases: boolean;
    assignPilotBasedOnKickoffDateOnly: boolean;
    releasePilotRotationRepeatCount: number;
}

const PilotRotationSettingsModal: React.FC<PilotRotationSettingsModalProps> = (props) => {
    const [form] = Form.useForm<PilotRotationSettingsFormValues>();
    const dispatch = useDispatch();

    const pilotRotationSettings = useSelector(selectPilotRotationSettings(props.appId));
    const schedulingIntegrations = useSelector((state: RootState) =>
        selectIntegrationsForCapability(state, props.appId, IntegrationCapability.Scheduling),
    );
    const isSyncEnabled = useSelector(selectIsPilotRotationSyncEnabled(props.appId));
    const assignsCurrentOnCallToAllActiveReleases = useSelector(
        selectShouldAssignCurrentOnCallToAllActiveReleases(props.appId),
    );
    const rotatesPilotForEachRelease = useSelector(selectShouldRotatePilotForEachReleaseFromSchedule(props.appId));
    const currentRepeatCount = useSelector(selectReleasePilotRotationRepeatCount(props.appId));

    const initialValues: Partial<PilotRotationSettingsFormValues> = {
        syncFromExternalSchedule: isSyncEnabled,
        schedulingInstallationId:
            (pilotRotationSettings?.schedulingInstallationId ?? schedulingIntegrations[0]?.installationId) || undefined,
        keepPreviousReleasePilotForNonStandardReleases:
            rotatesPilotForEachRelease || !pilotRotationSettings?.selectPilotFromRotationForNonStandardReleases,
        // Reflect actual behavior: the internal-only config already hands every active release to the current on-call
        assignPilotBasedOnKickoffDateOnly:
            rotatesPilotForEachRelease ||
            (!assignsCurrentOnCallToAllActiveReleases &&
                !pilotRotationSettings?.alwaysAssignCurrentOnCallToNextRelease),
        syncScheduleMembersToPilotGroup: !!pilotRotationSettings?.syncScheduleMembersToPilotGroup,
        releasePilotRotationRepeatCount: currentRepeatCount,
    };

    const syncFromExternalSchedule = Form.useWatch('syncFromExternalSchedule', form);
    const noSchedulingIntegrations = schedulingIntegrations.length === 0;

    const isLoading = useIsLoading(AppSettingsV2Updatable.UpdatePilotRotationSettings);
    const success = useUpdated(AppSettingsV2Updatable.UpdatePilotRotationSettings);
    const error = useError(AppSettingsV2Updatable.UpdatePilotRotationSettings);

    React.useEffect(() => {
        if (success) {
            globalMessage.success('Release pilot rotation settings updated.');
            props.onCancel();
        }
    }, [success]);

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error);
        }
    }, [error]);

    const savePilotRotationSettings = (values: PilotRotationSettingsFormValues) => {
        const request: UpdatePilotRotationSettingsRequest = {
            appId: props.appId,
            orgId: props.orgId,
            workflowId: props.workflowId,
            syncFromExternalSchedule: values.syncFromExternalSchedule,
            schedulingInstallationId: values.syncFromExternalSchedule ? values.schedulingInstallationId : '',
            selectPilotFromRotationForNonStandardReleases: !values.keepPreviousReleasePilotForNonStandardReleases,
            alwaysAssignCurrentOnCallToNextRelease: alwaysAssignCurrentOnCallToNextRelease(values),
            syncScheduleMembersToPilotGroup:
                values.syncFromExternalSchedule && !!values.syncScheduleMembersToPilotGroup,
            releasePilotRotationRepeatCount: values.releasePilotRotationRepeatCount ?? currentRepeatCount,
        };
        dispatch(tryUpdateAppSettingsV2(AppSettingsV2Updatable.UpdatePilotRotationSettings, request));
    };

    const alwaysAssignCurrentOnCallToNextRelease = (values: PilotRotationSettingsFormValues) => {
        if (!values.syncFromExternalSchedule || rotatesPilotForEachRelease) {
            return false;
        }
        if (assignsCurrentOnCallToAllActiveReleases) {
            return !!pilotRotationSettings?.alwaysAssignCurrentOnCallToNextRelease;
        }
        return !values.assignPilotBasedOnKickoffDateOnly;
    };

    return (
        <Modal
            title="Release pilot rotation settings"
            open={props.open}
            onCancel={props.onCancel}
            destroyOnClose
            footer={[
                <Button key="cancel" onClick={props.onCancel}>
                    Cancel
                </Button>,
                <PermissionedActionButton
                    key="save"
                    type="primary"
                    actionType={RunwayUserAction.UAUpdateAppSettings}
                    loading={isLoading}
                    onClick={() => form.submit()}
                >
                    Save
                </PermissionedActionButton>,
            ]}
        >
            <Form
                form={form}
                onFinish={savePilotRotationSettings}
                layout="vertical"
                preserve={false}
                initialValues={initialValues}
            >
                <Tooltip
                    title="Connect a scheduling integration to sync the rotation from an on-call schedule"
                    open={noSchedulingIntegrations ? undefined : false}
                >
                    <Form.Item name="syncFromExternalSchedule" valuePropName="checked" style={{ marginBottom: 8 }}>
                        <Checkbox disabled={noSchedulingIntegrations && !syncFromExternalSchedule}>
                            Sync from external schedule
                        </Checkbox>
                    </Form.Item>
                </Tooltip>
                <Form.Item
                    label=""
                    name="schedulingInstallationId"
                    rules={[{ required: !!syncFromExternalSchedule, message: 'Please select an integration' }]}
                    style={{ paddingLeft: 24 }}
                >
                    <Select
                        disabled={!syncFromExternalSchedule}
                        style={{ opacity: syncFromExternalSchedule ? 1 : 0.5 }}
                        options={integrationSelectOptions(schedulingIntegrations)}
                        placeholder="Select an integration"
                        optionFilterProp="title"
                    />
                </Form.Item>
                {syncFromExternalSchedule && (
                    <Tooltip
                        title={
                            rotatesPilotForEachRelease ?
                                rotatesPilotForEachReleaseOverrideMessage
                            :   <>
                                    Your app has an existing internal configuration that overrides this setting. On-call
                                    changes currently update the pilot continuously for all active releases.
                                    <br />
                                    <br />
                                    Contact us if you&apos;d like to remove this override.
                                </>
                        }
                        open={assignsCurrentOnCallToAllActiveReleases || rotatesPilotForEachRelease ? undefined : false}
                    >
                        <Form.Item
                            name="assignPilotBasedOnKickoffDateOnly"
                            valuePropName="checked"
                            style={{ paddingLeft: 24 }}
                        >
                            <Checkbox disabled={assignsCurrentOnCallToAllActiveReleases || rotatesPilotForEachRelease}>
                                Assign release pilot based on kickoff date only
                                <TooltipInfoIcon
                                    tooltipContent={
                                        <>
                                            When enabled, the external schedule's on-call person at the time of kickoff
                                            remains assigned as the pilot in Runway for the entire release, regardless
                                            of the external schedule's on-call assignments after kickoff.
                                            <br />
                                            <br />
                                            When disabled, the pilot will be continuously updated in Runway based on the
                                            external schedule's on-call assignment.
                                        </>
                                    }
                                    style={{ marginLeft: 8 }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                />
                            </Checkbox>
                        </Form.Item>
                    </Tooltip>
                )}
                {syncFromExternalSchedule && (
                    <Form.Item
                        name="syncScheduleMembersToPilotGroup"
                        valuePropName="checked"
                        style={{ paddingLeft: 24 }}
                    >
                        <Checkbox>
                            Sync rotation members to the &apos;Release pilot&apos; group
                            <TooltipInfoIcon
                                tooltipContent={
                                    <>
                                        Members on the external schedule are added to the &apos;Release pilot&apos;
                                        group, and are removed from the group if not present.
                                        <br />
                                        <br />
                                        Members manually added to the group will not be removed via this sync.
                                        <br />
                                        <br />
                                        This sync is not performed when users are managed by Directory Sync.
                                    </>
                                }
                                style={{ marginLeft: 8 }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            />
                        </Checkbox>
                    </Form.Item>
                )}
                <Tooltip
                    title={rotatesPilotForEachReleaseOverrideMessage}
                    open={rotatesPilotForEachRelease ? undefined : false}
                >
                    <Form.Item name="keepPreviousReleasePilotForNonStandardReleases" valuePropName="checked">
                        <Checkbox disabled={rotatesPilotForEachRelease}>
                            Keep previous release pilot for hotfixes and rollbacks
                        </Checkbox>
                    </Form.Item>
                </Tooltip>
                {!syncFromExternalSchedule && (
                    <Form.Item
                        name="releasePilotRotationRepeatCount"
                        label={
                            <>
                                Consecutive releases per pilot
                                <TooltipInfoIcon
                                    tooltipContent="Each pilot stays assigned for this many consecutive releases before the rotation advances to the next pilot. Hotfixes and rollbacks don't count toward this."
                                    style={{ marginLeft: 8 }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    tooltipProps={{ placement: 'right' }}
                                />
                            </>
                        }
                        rules={[
                            {
                                required: true,
                                type: 'number',
                                min: 1,
                                message: 'Please enter a number of at least 1',
                            },
                        ]}
                    >
                        <InputNumber min={1} precision={0} style={{ width: 80 }} />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

export default PilotRotationSettingsModal;
